import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// hooks
import { useTemplateFields } from 'hooks/useTemplateFields'
// graphql
import { CHART_DATA_QUERY, CHART_PREVIEW_QUERY } from 'graphql/chart/queries'
// types
import { ChartConfig } from 'types/charts'
import { IField, IChartData, ChartAggregate, ChartDataStatus, IChartDataInput } from 'types/graphql'
// constants
import { AGGREGATE_OPTIONS } from 'constants/charts'
// utils
import { keyBy } from 'lodash'
import { prepareChartData } from 'utils/charts/rows'
import { buildChartOption } from 'utils/charts/option'
import { getChartDataInput, isChartQueryReady } from 'utils/charts/config'

const getValueName = (config: ChartConfig, fieldsById: Record<string, IField>) => {
  const { aggregate, aggregateFieldId } = config.query

  if (aggregate === ChartAggregate.Count) {
    return 'Count'
  }

  const label = AGGREGATE_OPTIONS.find((item) => item.value === aggregate)?.label

  return `${label} of ${fieldsById[aggregateFieldId]?.label || 'value'}`
}

interface Options {
  // A saved chart reads the viewer's snapshot; without it the config's query is previewed live
  chartId?: string
  title?: string
}

// What ChartView draws: the chart's rows, labelled from the template fields, as an ECharts option, and its state
export const useChartView = (config: ChartConfig | null, { chartId, title }: Options = {}) => {
  const isReady = isChartQueryReady(config)

  const { fields, loading: fieldsLoading } = useTemplateFields(config?.query.riskTemplateId)

  const saved = useQuery<{ chartData: IChartData }>(CHART_DATA_QUERY, {
    variables: { chartId },
    skip: !isReady || !chartId,
    // The cached numbers show at once, and the server is asked anyway: only its answer queues the recompute of a
    // stale snapshot and tells a snapshot dropped since (COMPUTING), one recomputing (REFRESHING) or a failed one
    fetchPolicy: 'cache-and-network'
  })

  const preview = useQuery<{ chartPreview: IChartData }, { input: IChartDataInput }>(CHART_PREVIEW_QUERY, {
    variables: { input: isReady ? getChartDataInput(config.query) : undefined },
    skip: !isReady || !!chartId,
    // A setting turned back shows its numbers at once, while the records are counted again
    fetchPolicy: 'cache-and-network'
  })

  const { loading, error } = chartId ? saved : preview

  // The previous rows stay on screen while the next ones load, so editing the settings never blanks the chart
  const response = chartId
    ? (saved.data || saved.previousData)?.chartData
    : (preview.data || preview.previousData)?.chartPreview
  const rows = response?.rows

  // No numbers yet: empty rows until a CHART_DATA_UPDATED notification brings the first ones
  const isComputing = response?.status === ChartDataStatus.Computing

  const option = React.useMemo(() => {
    if (!isReady || !rows || isComputing) {
      return null
    }

    const fieldsById = keyBy(fields, 'id')
    const lookupLabels = Object.fromEntries((response.labels || []).map(({ id, label }) => [id, label]))
    const chartData = prepareChartData(rows, lookupLabels, config, fieldsById, getValueName(config, fieldsById))

    return buildChartOption(config, chartData, title)
  }, [response, rows, fields, config, title, isReady, isComputing])

  // The new numbers are on their way: REFRESHING keeps the old ones on screen, COMPUTING has none yet
  const isPending = isComputing || response?.status === ChartDataStatus.Refreshing

  return {
    option,
    error,
    isReady,
    // By the records matched, not the rows: the server fills in every combination of keys, at 0 or null with no records.
    // A snapshot still computing has no total yet: the loader shows, not "no records"
    isEmpty: !!response && !isPending && !response.total,
    // SUM / AVG / MIN / MAX of a number field no matched record has filled in
    hasNoValues: !isPending && !!rows?.length && rows.every((row) => row.value === null || row.value === undefined),
    isPending,
    loading: loading || fieldsLoading
  }
}
